import { ArrowLeftIcon, CubeIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import groupBy from "lodash/groupBy";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { selectActivePremiumsWithPositiveValues, selectPremiumCommodities } from "@/redux/positions/positionsSelectors";
import type { TReduxPremium } from "@/redux/positions/positionsTypes";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import { useGetLondonFixQuery } from "@/shared/api/londonFix";
import MetalPageControls from "@/shared/components/MetalPageControls";
import type { AssetClass } from "@/shared/components/PageControls";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { CardContent } from "@/shared/components/ui/card";
import { CardHeader } from "@/shared/components/ui/card";
import { CardTitle } from "@/shared/components/ui/card";
import { Pagination } from "@/shared/components/ui/pagination";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Table } from "@/shared/components/ui/table";
import { TableBody } from "@/shared/components/ui/table";
import { TableCell } from "@/shared/components/ui/table";
import { TableFooter } from "@/shared/components/ui/table";
import { TableHead } from "@/shared/components/ui/table";
import { TableHeader } from "@/shared/components/ui/table";
import { TableRow } from "@/shared/components/ui/table";
import useActiveAssetClass from "@/shared/hooks/useActiveAssetClass";
import { ASSET_LABELS } from "@/util/assetLabels";
import { formatCurrency, formatOz } from "@/util/numberUtils";

const ITEMS_PER_PAGE = 10;

const PremiumsSkeleton = () => (
  <div className="flex flex-col gap-4 md:gap-9">
    <Skeleton className="h-14 rounded md:h-[104px] md:rounded-2xl" />
    <Skeleton className="h-[400px] rounded-2xl" />
  </div>
);

const PremiumsEmptyState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <CubeIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`You don't currently have any premium products on account.`}</p>
      <Button color="primary" render={<Link to="/balance" />}>
        <ArrowLeftIcon className="mr-2 h-5 w-auto" />
        {t`Back to Balance`}
      </Button>
    </div>
  );
};

const getAppearanceBadgeVariant = (appearance: string) => {
  switch (appearance) {
    case "coin":
      return "default";
    case "bar":
      return "secondary";
    default:
      return "outline";
  }
};

const getAppearanceLabel = (appearance: string) => {
  switch (appearance) {
    case "coin":
      return "Coin";
    case "bar":
      return "Bar";
    case "misc":
      return "Other";
    default:
      return appearance;
  }
};

type ProductDetailsProps = {
  custodianCode: string;
  vaultProduct: string;
  brand?: string;
  serial?: string;
};

const ProductDetails = ({ custodianCode, vaultProduct, brand, serial }: ProductDetailsProps) => {
  const hasBrand = brand && brand.trim().length > 0;
  const hasSerial = serial && serial.trim().length > 0;

  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <div className="font-medium text-foreground">
        {custodianCode}@{vaultProduct}
      </div>
      {(hasBrand || hasSerial) && (
        <div className="flex items-center gap-2 text-[11px] whitespace-nowrap text-muted-foreground">
          {hasBrand && (
            <Trans>
              <span className="whitespace-nowrap">
                Brand <span className="font-medium text-foreground uppercase">{brand}</span>
              </span>
            </Trans>
          )}
          {hasBrand && hasSerial && <span className="text-muted-foreground/60">•</span>}
          {hasSerial && (
            <Trans>
              <span className="min-w-0 whitespace-nowrap">
                Serial&nbsp;
                <span
                  title={serial}
                  className="inline-block max-w-[140px] truncate align-bottom font-mono text-foreground"
                >
                  {serial}
                </span>
              </span>
            </Trans>
          )}
        </div>
      )}
    </div>
  );
};

type PremiumTableProps = {
  premiums: TReduxPremium[];
  commodity: string;
};

const PremiumTable = ({ premiums, commodity }: PremiumTableProps) => {
  const { data: londonFixData } = useGetLondonFixQuery();
  const [page, setPage] = useState(1);

  const londonFixPrice = londonFixData?.[commodity as keyof typeof londonFixData]?.price ?? 0;

  // Calculate totals for footer
  const totals = useMemo(() => {
    return premiums.reduce(
      (acc, premium) => {
        const depositWeight = premium["premium/deposit"] || 0;
        const storageWeight = premium["premium/storage"] || 0;
        const totalWeight = depositWeight + storageWeight;
        const totalValue = totalWeight * londonFixPrice;

        return {
          weight: acc.weight + totalWeight,
          value: acc.value + totalValue,
        };
      },
      { weight: 0, value: 0 },
    );
  }, [premiums, londonFixPrice]);

  // Paginated premiums
  const paginatedPremiums = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return premiums.slice(start, start + ITEMS_PER_PAGE);
  }, [premiums, page]);

  const totalPages = Math.ceil(premiums.length / ITEMS_PER_PAGE);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <CardTitle className="text-primary">{ASSET_LABELS[commodity as keyof typeof ASSET_LABELS]}</CardTitle>
          <span className="text-sm text-muted-foreground">{t`${premiums.length} product on account`}</span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead className="w-[40%]">{t`Product`}</TableHead>
              <TableHead className="w-[25%]">{t`Details`}</TableHead>
              <TableHead className="w-[17%] text-right">{t`Value (oz)`}</TableHead>
              <TableHead className="w-[18%] text-right">{t`Value (USD)`}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedPremiums.map((premium, index) => {
              const depositWeight = premium["premium/deposit"] || 0;
              const storageWeight = premium["premium/storage"] || 0;
              const totalWeight = depositWeight + storageWeight;
              const totalValue = totalWeight * londonFixPrice;
              const appearance = premium.asset_details.asset_appearance;

              return (
                <TableRow
                  key={premium.assetCode}
                  className={index % 2 === 0 ? "bg-background hover:bg-background" : "bg-muted/10 hover:bg-muted/10"}
                >
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="leading-tight font-medium">{premium.asset_details.asset_xdesc}</span>
                      <Badge variant={getAppearanceBadgeVariant(appearance)} className="w-fit text-xs">
                        {getAppearanceLabel(appearance)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ProductDetails
                      custodianCode={premium.asset_details.custodian_code}
                      vaultProduct={premium.asset_details.vault_product}
                      brand={premium.asset_details.brand}
                      serial={premium.asset_details.serial}
                    />
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{formatOz(totalWeight)}</TableCell>
                  <TableCell className="text-right font-semibold text-primary tabular-nums">
                    {formatCurrency(totalValue)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-muted/30 font-semibold">
              <TableCell colSpan={2}>{t`Total`}</TableCell>
              <TableCell className="text-right tabular-nums">{formatOz(totals.weight)} oz</TableCell>
              <TableCell className="text-right text-primary tabular-nums">{formatCurrency(totals.value)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        {totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination page={page} count={totalPages} onChange={setPage} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function PremiumsView() {
  const eid = useSelector(selectMaybeActiveAccountEid);
  const positionQuery = useGetAccountPositionQuery(eid || skipToken);
  const [activeAssetClass] = useActiveAssetClass();

  const allPremiums = useSelector(selectActivePremiumsWithPositiveValues);
  const premiumCommodities = useSelector(selectPremiumCommodities);

  const supportedAssets = useMemo(() => {
    const assets: AssetClass[] = [...premiumCommodities];
    if (assets.length > 1) {
      assets.unshift("total");
    }
    return assets;
  }, [premiumCommodities]);

  const premiumsByCommodity = useMemo(() => groupBy(allPremiums, "commodity"), [allPremiums]);

  const commoditiesToShow = useMemo(() => {
    if (activeAssetClass === "total") {
      return premiumCommodities;
    }
    return premiumCommodities.filter((c) => c === activeAssetClass);
  }, [activeAssetClass, premiumCommodities]);

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<PremiumsSkeleton />}
      shouldRetry
      queries={[positionQuery]}
      translatedErrorMessage={t`We are having trouble loading your premium products. Please try again later or contact our support team if the issue persists.`}
    >
      {allPremiums.length === 0 ? (
        <PremiumsEmptyState />
      ) : (
        <div className="flex flex-col gap-4 md:gap-9">
          <div className="-mb-2 flex items-center gap-4">
            <Button variant="ghost" size="sm" render={<Link to="/balance" />}>
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              {t`Back to Balance`}
            </Button>
          </div>
          {supportedAssets.length > 1 && <MetalPageControls supportedAssets={supportedAssets} />}
          <div className="flex flex-col gap-4">
            {commoditiesToShow.map((commodity) => (
              <PremiumTable key={commodity} commodity={commodity} premiums={premiumsByCommodity[commodity] || []} />
            ))}
          </div>
        </div>
      )}
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/balance/premiums")({
  component: PremiumsView,
  context: () => ({ permissions: "balance.premiums" }),
});
